import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Breadcrumb from "./Breadcrumb";
import { api } from "../services/api";
import { toast } from "react-toastify";

const AddCategoriesLayer = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);

  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
  });

  const [newSubcategory, setNewSubcategory] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch all categories when component mounts
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.categories.getAll();
      if (response.success) {
        setCategories(response.data);
      } else {
        toast.error(response.message || "Failed to fetch categories");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("An error occurred while fetching categories");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!newCategory.name) newErrors.name = "Category name is required";
    if (!newCategory.description)
      newErrors.description = "Description is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewCategory((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubcategoryChange = (e) => {
    setNewSubcategory(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      try {
        setLoading(true);

        let response;
        if (isEditing) {
          response = await api.categories.update(
            editingCategoryId,
            newCategory
          );
        } else {
          response = await api.categories.create(newCategory);
        }

        if (response.success) {
          toast.success(
            response.message ||
              (isEditing
                ? "Category updated successfully"
                : "Category created successfully")
          );

          // Reset form
          setNewCategory({
            name: "",
            description: "",
          });
          setIsEditing(false);
          setEditingCategoryId(null);
          setErrors({});

          // Refresh categories list
          fetchCategories();
        } else {
          toast.error(response.message || "Failed to save category");
        }
      } catch (error) {
        console.error("Error saving category:", error);
        toast.error("An error occurred while saving the category");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (category) => {
    setNewCategory({
      name: category.name,
      description: category.description || "",
    });
    setIsEditing(true);
    setEditingCategoryId(category.id);
  };

  const handleCancel = () => {
    setNewCategory({
      name: "",
      description: "",
    });
    setIsEditing(false);
    setEditingCategoryId(null);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        setLoading(true);
        const response = await api.categories.delete(id);

        if (response.success) {
          toast.success(response.message || "Category deleted successfully");
          fetchCategories();
        } else {
          toast.error(response.message || "Failed to delete category");
        }
      } catch (error) {
        console.error("Error deleting category:", error);
        toast.error("An error occurred while deleting the category");
      } finally {
        setLoading(false);
      }
    }
  };

  const openSubcategoryModal = async (category) => {
    try {
      setLoading(true);
      const response = await api.categories.getSubcategories(category.id);

      if (response.success) {
        setSelectedCategory({
          ...category,
          subcategories: response.data,
        });
      } else {
        toast.error(response.message || "Failed to fetch subcategories");
        setSelectedCategory(category);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.error("An error occurred while fetching subcategories");
      setSelectedCategory(category);
    } finally {
      setLoading(false);
      setNewSubcategory("");
      setShowSubcategoryModal(true);
    }
  };

  const closeSubcategoryModal = () => {
    setSelectedCategory(null);
    setShowSubcategoryModal(false);
  };

  const addSubcategoryToExisting = async () => {
    if (!selectedCategory || newSubcategory.trim() === "") return;

    try {
      setLoading(true);
      const response = await api.categories.createSubcategory(
        selectedCategory.id,
        {
          name: newSubcategory.trim(),
        }
      );

      if (response.success) {
        toast.success(response.message || "Subcategory added successfully");

        // Update the selected category with the new subcategory
        setSelectedCategory({
          ...selectedCategory,
          subcategories: [...selectedCategory.subcategories, response.data],
        });

        setNewSubcategory("");
        fetchCategories(); // Refresh the categories list
      } else {
        toast.error(response.message || "Failed to add subcategory");
      }
    } catch (error) {
      console.error("Error adding subcategory:", error);
      toast.error("An error occurred while adding the subcategory");
    } finally {
      setLoading(false);
    }
  };

  const removeSubcategoryFromExisting = async (subcategoryId) => {
    if (!selectedCategory) return;

    try {
      setLoading(true);
      const response = await api.categories.deleteSubcategory(subcategoryId);

      if (response.success) {
        toast.success(response.message || "Subcategory removed successfully");

        // Update the selected category by removing the deleted subcategory
        setSelectedCategory({
          ...selectedCategory,
          subcategories: selectedCategory.subcategories.filter(
            (sub) => sub.id !== subcategoryId
          ),
        });

        fetchCategories(); // Refresh the categories list
      } else {
        toast.error(response.message || "Failed to remove subcategory");
      }
    } catch (error) {
      console.error("Error removing subcategory:", error);
      toast.error("An error occurred while removing the subcategory");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12">
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="row align-items-center">
                <div className="col-12">
                  <Breadcrumb pageName="Categories Management" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 mt-4">
          <div className="card border-0 shadow-sm overflow-hidden">
            <div className="card-header py-3 bg-soft-primary">
              <h5 className="card-title mb-0">
                {isEditing ? "Update Category" : "Add New Category"}
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="categoryName" className="form-label">
                      Category Name
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        errors.name ? "is-invalid" : ""
                      }`}
                      id="categoryName"
                      name="name"
                      value={newCategory.name}
                      onChange={handleChange}
                      placeholder="Enter category name"
                      disabled={loading}
                    />
                    {errors.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        errors.description ? "is-invalid" : ""
                      }`}
                      id="description"
                      name="description"
                      value={newCategory.description}
                      onChange={handleChange}
                      placeholder="Enter category description"
                      disabled={loading}
                    />
                    {errors.description && (
                      <div className="invalid-feedback">
                        {errors.description}
                      </div>
                    )}
                  </div>

                  <div className="col-12">
                    <button
                      type="submit"
                      className="btn btn-primary me-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <Icon
                            icon={
                              isEditing ? "akar-icons:edit" : "akar-icons:plus"
                            }
                            className="me-2"
                          />{" "}
                          {isEditing ? "Update" : "Add"} Category
                        </>
                      )}
                    </button>

                    {isEditing && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12 mt-4">
          <div className="card border-0 shadow-sm overflow-hidden">
            <div className="card-header py-3 bg-soft-primary">
              <h5 className="card-title mb-0">Categories List</h5>
            </div>
            <div className="card-body">
              {loading && !categories.length ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading categories...</p>
                </div>
              ) : categories.length === 0 ? (
                <div className="alert alert-info">
                  No categories found. Add your first category.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Category Name</th>
                        <th scope="col">Description</th>
                        <th scope="col">Subcategories</th>
                        <th scope="col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((category, index) => (
                        <tr key={category.id}>
                          <th scope="row">{index + 1}</th>
                          <td>{category.name}</td>
                          <td>{category.description || "N/A"}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              <span className="badge bg-info me-1">
                                {category.subcategories?.length || 0}
                              </span>
                              <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => openSubcategoryModal(category)}
                                disabled={loading}
                              >
                                Manage
                              </button>
                            </div>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEdit(category)}
                              disabled={loading}
                            >
                              <Icon icon="akar-icons:edit" />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(category.id)}
                              disabled={loading}
                            >
                              <Icon icon="akar-icons:trash-can" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subcategory Modal */}
      {showSubcategoryModal && selectedCategory && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Manage Subcategories for {selectedCategory.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeSubcategoryModal}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Add New Subcategory</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter subcategory name"
                      value={newSubcategory}
                      onChange={handleSubcategoryChange}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={addSubcategoryToExisting}
                      disabled={loading || !newSubcategory.trim()}
                    >
                      {loading ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      ) : (
                        <Icon icon="akar-icons:plus" />
                      )}{" "}
                      Add
                    </button>
                  </div>
                </div>

                {loading && !selectedCategory.subcategories?.length ? (
                  <div className="text-center py-3">
                    <div
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading subcategories...</p>
                  </div>
                ) : selectedCategory.subcategories?.length > 0 ? (
                  <div>
                    <label className="form-label">Current Subcategories</label>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedCategory.subcategories.map((subcategory) => (
                        <div
                          key={subcategory.id}
                          className="badge bg-light text-dark d-flex align-items-center p-2"
                        >
                          {subcategory.name}
                          <button
                            type="button"
                            className="btn btn-sm text-danger ms-2 p-0"
                            onClick={() =>
                              removeSubcategoryFromExisting(subcategory.id)
                            }
                            disabled={loading}
                          >
                            <Icon icon="akar-icons:cross" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-info">
                    No subcategories added yet for this category.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeSubcategoryModal}
                  disabled={loading}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCategoriesLayer;
